/**
 * Tutors Service
 * 
 * Handles all tutor-related API calls.
 */

import api, { getApiUrl, getAuthToken } from '../utils/api';

/**
 * Get all tutors
 * @param {Object} params - Query parameters (skip, limit, approve, academic_field, etc.)
 * @returns {Promise} - Get tutors response
 */
export const getTutors = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.approve) queryParams.approve = params.approve;
    if (params.academic_field) queryParams.academic_field = params.academic_field;
    if (params.search) queryParams.search = params.search;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/tutors${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Tutors retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tutors',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get tutors error:', error);
    
    let errorMessage = 'Failed to retrieve tutors. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get a single tutor by ID
 * @param {string} tutorId - Tutor ID
 * @returns {Promise} - Get tutor response
 */
export const getTutor = async (tutorId) => {
  try {
    const response = await api.get(`/v1/tutors/${tutorId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tutor retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tutor',
      data: null,
    };
  } catch (error) {
    console.error('Get tutor error:', error);
    
    let errorMessage = 'Failed to retrieve tutor. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update a tutor application (full update using PUT)
 * @param {string} tutorId - Tutor ID
 * @param {Object} tutorData - Tutor data to update
 * @returns {Promise} - Update tutor response
 */
export const updateTutor = async (tutorId, tutorData) => {
  try {
    const response = await api.put(`/v1/tutors/${tutorId}`, tutorData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tutor updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update tutor',
      data: null,
    };
  } catch (error) {
    console.error('Update tutor error:', error);
    
    let errorMessage = 'Failed to update tutor. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Partially update a tutor application (PATCH)
 * @param {string} tutorId - Tutor ID
 * @param {Object} tutorData - Fields to update (only include fields to update)
 * @returns {Promise} - Update tutor response
 */
export const updateTutorPartial = async (tutorId, tutorData) => {
  try {
    const response = await api.patch(`/v1/tutors/${tutorId}`, tutorData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tutor updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update tutor',
      data: null,
    };
  } catch (error) {
    console.error('Update tutor partial error:', error);
    
    let errorMessage = 'Failed to update tutor. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete a tutor application
 * @param {string} tutorId - Tutor ID
 * @returns {Promise} - Delete tutor response
 */
export const deleteTutor = async (tutorId) => {
  try {
    const response = await api.delete(`/v1/tutors/${tutorId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Tutor deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete tutor',
    };
  } catch (error) {
    console.error('Delete tutor error:', error);
    
    let errorMessage = 'Failed to delete tutor. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Approve a tutor application
 * @param {string} tutorId - Tutor ID
 * @param {Object} approvalData - Optional approval data (approval_notes)
 * @returns {Promise} - Approve tutor response
 */
export const approveTutor = async (tutorId, approvalData = {}) => {
  try {
    const response = await api.post(`/v1/tutors/${tutorId}/approve`, approvalData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tutor approved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to approve tutor',
      data: null,
    };
  } catch (error) {
    console.error('Approve tutor error:', error);
    
    let errorMessage = 'Failed to approve tutor. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Reject a tutor application
 * @param {string} tutorId - Tutor ID
 * @param {Object} rejectionData - Optional rejection data (approval_notes)
 * @returns {Promise} - Reject tutor response
 */
export const rejectTutor = async (tutorId, rejectionData = {}) => {
  try {
    const response = await api.post(`/v1/tutors/${tutorId}/reject`, rejectionData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Tutor rejected successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to reject tutor',
      data: null,
    };
  } catch (error) {
    console.error('Reject tutor error:', error);
    
    let errorMessage = 'Failed to reject tutor. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Get all tutors for export (without pagination)
 * @param {Object} params - Query parameters (approve, academic_field, etc.)
 * @returns {Promise} - Get all tutors response
 */
export const getAllTutors = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.approve) queryParams.approve = params.approve;
    if (params.academic_field) queryParams.academic_field = params.academic_field;
    if (params.search) queryParams.search = params.search;
    
    // Fetch all tutors by making multiple requests if needed
    // Start with a reasonable limit
    const limit = 100;
    let skip = 0;
    let allTutors = [];
    let hasMore = true;

    while (hasMore) {
      queryParams.limit = limit;
      queryParams.skip = skip;

      const queryString = new URLSearchParams(queryParams).toString();
      const endpoint = `/v1/tutors${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);

      if (response.status === 'success' && response.data) {
        const tutors = response.data;
        allTutors = [...allTutors, ...tutors];
        
        // Check if we got all records
        if (tutors.length < limit || !response.pagination || allTutors.length >= response.pagination.total) {
          hasMore = false;
        } else {
          skip += limit;
        }
      } else {
        // If first request fails, return error
        if (skip === 0) {
          return {
            success: false,
            error: response.message || 'Failed to retrieve tutors',
            data: null,
          };
        }
        // If subsequent request fails, return what we have
        hasMore = false;
      }
    }

    return {
      success: true,
      data: allTutors,
      message: 'Tutors retrieved successfully',
    };
  } catch (error) {
    console.error('Get all tutors error:', error);
    
    let errorMessage = 'Failed to retrieve tutors. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Export tutors to PDF using backend endpoint
 * @param {Object} params - Query parameters (approve, department, search)
 * @returns {Promise} - PDF blob download
 */
export const exportTutorsPDF = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.approve && params.approve !== 'all') {
      queryParams.approve = params.approve;
    }
    if (params.department) {
      queryParams.department = params.department;
    }
    if (params.search) {
      queryParams.search = params.search;
    }

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/tutors/export/pdf${queryString ? `?${queryString}` : ''}`;
    
    // Get API URL and token
    const url = getApiUrl(endpoint);
    const token = getAuthToken();

    // Fetch PDF as blob
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/pdf',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'tutor_applications.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Convert response to blob
    const blob = await response.blob();

    // Create download link and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return {
      success: true,
      message: 'PDF exported successfully',
    };
  } catch (error) {
    console.error('Export tutors PDF error:', error);
    
    let errorMessage = 'Failed to export PDF. Please try again.';
    
    if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

const tutorsService = {
  getTutors,
  getTutor,
  getAllTutors,
  updateTutor,
  updateTutorPartial,
  deleteTutor,
  approveTutor,
  rejectTutor,
  exportTutorsPDF,
};
export default tutorsService;

